import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
} from 'react';
import { getClonedChild, Group, Box, Flex } from '@landr/maestro';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import ConditionalLink from 'components/ConditionalLink/ConditionalLink';
import Actions from './Actions/Actions';
import BackgroundImage from './BackgroundImage/BackgroundImage';
import Title from './Title/Title';
import Byline from './Byline/Byline';
import { CardProps } from './Card.types';

interface CardSubComponents {
    Actions: typeof Actions;
    BackgroundImage: typeof BackgroundImage;
    Title: typeof Title;
    Byline: typeof Byline;
}

type CardComponent = ForwardRefExoticComponent<
    CardProps & RefAttributes<HTMLElement>
> &
    CardSubComponents;

const Card = forwardRef<
    HTMLDivElement,
    CardProps & React.RefAttributes<HTMLDivElement>
>(
    (
        {
            children,
            linkTo,
            bylineLinkTo,
            isExclusive,
            backgroundColor = 'surface.low',
            isLoading = false,
            onLinkClick,
        },
        ref,
    ) => {
        const childrenArray = React.Children.toArray(children);

        const backgroundImage = getClonedChild(
            childrenArray,
            Card.BackgroundImage,
            {
                key: 'backgroundImage',
                isLoading: isLoading,
            },
        );
        const actions = getClonedChild(childrenArray, Card.Actions, {
            key: 'actions',
            isLoading: isLoading,
        });
        const title = getClonedChild(childrenArray, Card.Title, {
            key: 'title',
            isLoading: isLoading,
        });
        const byline = getClonedChild(childrenArray, Card.Byline, {
            key: 'byline',
            isLoading: isLoading,
        });

        return (
            <Box
                ref={ref}
                userSelect="none"
                position="relative"
                width="100%"
                height="auto"
            >
                <Group
                    direction="vertical"
                    gap="2xs"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    <Group.Item height="auto">
                        <ConditionalLink to={linkTo} onLinkClick={onLinkClick}>
                            <Group direction="vertical" gap="md">
                                <Group.Item>
                                    <Box
                                        position="relative"
                                        paddingBottom="100%"
                                        borderRadius="base"
                                        overflow="hidden"
                                        style={{ backgroundColor }}
                                    >
                                        {backgroundImage}
                                        {isExclusive && (
                                            <ExclusivePackBadge
                                                isAbsolute={true}
                                            />
                                        )}
                                        {actions}
                                    </Box>
                                </Group.Item>
                                <Group.Item>
                                    <Flex
                                        width="100%"
                                        whiteSpace="nowrap"
                                        textOverflow="ellipsis"
                                        overflow="hidden"
                                        textStyle="body"
                                        _hover={{
                                            textDecoration: 'underline',
                                        }}
                                    >
                                        {title}
                                    </Flex>
                                </Group.Item>
                            </Group>
                        </ConditionalLink>
                    </Group.Item>
                    <Group.Item>
                        <ConditionalLink to={bylineLinkTo}>
                            <Flex
                                width="100%"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                                overflow="hidden"
                                _hover={{
                                    textDecoration: 'underline',
                                    textDecorationColor: 'text.medium',
                                }}
                            >
                                {byline}
                            </Flex>
                        </ConditionalLink>
                    </Group.Item>
                </Group>
            </Box>
        );
    },
) as CardComponent;

Card.BackgroundImage = BackgroundImage;
Card.Actions = Actions;
Card.Title = Title;
Card.Byline = Byline;

export default Card;
